<template>
  <FlashNotifications ref="flash" />

  <!-- ═══════════════ MOBILE ═══════════════ -->
  <div class="sm:hidden bg-gray-50 dark:bg-[#0d1117] -mx-5 pt-4 pb-20">

    <!-- Organization details -->
    <p class="px-4 pb-2 text-[11px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider">{{ I18N.website_content.account_settings.account_tab.title }}</p>
    <div class="bg-white dark:bg-[#1C2128] rounded-2xl mx-4 overflow-hidden divide-y divide-gray-100 dark:divide-gray-700/50">
      <div class="px-4 py-3">
        <label class="block text-[10px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-1">{{ I18N.features.organization.address }}</label>
        <textarea :value="org.address"
                  @focusout="updateOrgField"
                  name="organization[address]" rows="3"
                  class="w-full text-sm text-gray-900 dark:text-gray-100 bg-transparent focus:outline-none resize-none placeholder-gray-300 dark:placeholder-gray-600 leading-relaxed"></textarea>
      </div>
      <div class="px-4 py-3">
        <label class="block text-[10px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-1">{{ I18N.features.organization.tag_line }}</label>
        <input :value="org.tag_line"
               @focusout="updateOrgField"
               name="organization[tag_line]" type="text"
               class="w-full text-sm text-gray-900 dark:text-gray-100 bg-transparent focus:outline-none placeholder-gray-300 dark:placeholder-gray-600" />
      </div>
    </div>

    <!-- Branding -->
    <p class="px-4 pt-6 pb-2 text-[11px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider">{{ I18N.features.organization.platform_picture }}</p>
    <div class="bg-white dark:bg-[#1C2128] rounded-2xl mx-4 overflow-hidden px-4 py-5 flex flex-col items-center">
      <AvatarUploadCard
        ref="mobilePlatformCard"
        shape="rounded"
        :picture-url="org.platform_picture_url"
        :avatar-name="org.avatar_name"
        :avatar-color-class="org.avatar_color"
        :upload-url="`/api/v1/organizations/${org.id}/platform_picture`"
        :remove-url="`/api/v1/organizations/${org.id}/remove_platform_picture`"
        form-field-name="organization[platform_picture]"
        :title="I18N.features.organization.change_platform_picture"
        @updated="onPlatformPictureUpdated"
        @error="onAvatarError"
      />
      <button @click="$refs.mobilePlatformCard.openModal()" type="button"
        class="mt-3 px-5 py-1.5 bg-blue-600 text-white text-sm font-semibold rounded-full">
        Change logo
      </button>
      <p class="mt-2.5 text-center text-xs text-gray-400 dark:text-gray-500">{{ I18N.features.organization.dimensions }}: 300x100</p>
    </div>

    <!-- Danger zone -->
    <p class="px-4 pt-6 pb-2 text-[11px] font-semibold text-red-400 uppercase tracking-wider">{{ I18N.website_content.account_settings.account_tab.danger_zone }}</p>
    <div class="bg-white dark:bg-[#1C2128] rounded-2xl mx-4 overflow-hidden border border-red-100 dark:border-red-900/40 p-4">
      <p class="text-xs text-gray-500 dark:text-gray-400 mb-3">{{ I18N.features.organization.remove_org_subheading }}</p>
      <button @click="showDeleteModal = true" type="button"
        class="flex items-center gap-2 px-4 py-2 border border-red-300 dark:border-red-700 text-red-600 dark:text-red-400 text-sm font-semibold rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors">
        {{ I18N.features.organization.remove_org }}
      </button>
    </div>
  </div>

  <!-- ═══════════════ DESKTOP ═══════════════ -->
  <div class="hidden sm:block">

    <!-- Sub-page header -->
    <div class="bg-white dark:bg-[#1C2128] rounded-xl border border-gray-100 dark:border-gray-700 mb-4 flex items-center px-6 h-14">
      <div class="flex items-center gap-2.5">
        <div class="w-7 h-7 rounded-lg bg-blue-50 dark:bg-blue-900/20 flex items-center justify-center flex-shrink-0">
          <svg class="w-3.5 h-3.5 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" d="M9.594 3.94c.09-.542.56-.94 1.11-.94h2.593c.55 0 1.02.398 1.11.94l.213 1.281c.063.374.313.686.645.87.074.04.147.083.22.127.325.196.72.257 1.075.124l1.217-.456a1.125 1.125 0 0 1 1.37.49l1.296 2.247a1.125 1.125 0 0 1-.26 1.431l-1.003.827c-.293.241-.438.613-.43.992a7.723 7.723 0 0 1 0 .255c-.008.378.137.75.43.991l1.004.827c.424.35.534.955.26 1.43l-1.298 2.247a1.125 1.125 0 0 1-1.369.491l-1.217-.456c-.355-.133-.75-.072-1.076.124a6.47 6.47 0 0 1-.22.128c-.331.183-.581.495-.644.869l-.213 1.281c-.09.543-.56.94-1.11.94h-2.594c-.55 0-1.019-.398-1.11-.94l-.213-1.281c-.062-.374-.312-.686-.644-.87a6.52 6.52 0 0 1-.22-.127c-.325-.196-.72-.257-1.076-.124l-1.217.456a1.125 1.125 0 0 1-1.369-.49l-1.297-2.247a1.125 1.125 0 0 1 .26-1.431l1.004-.827c.292-.24.437-.613.43-.991a6.932 6.932 0 0 1 0-.255c.007-.38-.138-.751-.43-.992l-1.004-.827a1.125 1.125 0 0 1-.26-1.43l1.297-2.247a1.125 1.125 0 0 1 1.37-.491l1.216.456c.356.133.751.072 1.076-.124.072-.044.146-.086.22-.128.332-.183.582-.495.644-.869l.214-1.28Z"/>
            <path stroke-linecap="round" stroke-linejoin="round" d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z"/>
          </svg>
        </div>
        <div>
          <h1 class="text-sm font-semibold text-gray-900 dark:text-white leading-none">{{ I18N.website_content.account_settings.account_tab.title }}</h1>
          <p class="text-[11px] text-gray-400 dark:text-gray-500 mt-0.5 leading-none">Organization details and branding</p>
        </div>
      </div>
    </div>

    <div class="space-y-4">

      <!-- Organization details card -->
      <div class="bg-white dark:bg-[#1C2128] rounded-xl border border-gray-100 dark:border-gray-700 overflow-hidden">
        <div class="px-6 py-4 border-b border-gray-100 dark:border-gray-700 flex items-center gap-3">
          <div class="w-6 h-6 rounded-md bg-gray-100 dark:bg-gray-800 flex items-center justify-center flex-shrink-0">
            <svg class="w-3.5 h-3.5 text-gray-500 dark:text-gray-400" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" d="M2.25 21h19.5m-18-18v18m10.5-18v18m6-13.5V21M6.75 6.75h.75m-.75 3h.75m-.75 3h.75m3-6h.75m-.75 3h.75m-.75 3h.75M6.75 21v-3.375c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21"/>
            </svg>
          </div>
          <div>
            <h2 class="text-sm font-semibold text-gray-900 dark:text-white leading-none">{{ I18N.website_content.account_settings.account_tab.title }}</h2>
            <p class="text-[11px] text-gray-400 dark:text-gray-500 mt-0.5 leading-none">Shown on your organization's public profile page</p>
          </div>
        </div>
        <div class="px-6 py-5 space-y-4">
          <div>
            <label for="org_address" class="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1.5">{{ I18N.features.organization.address }}</label>
            <textarea :value="org.address"
                      @focusout="updateOrgField"
                      name="organization[address]" id="org_address" rows="3"
                      class="w-full px-3.5 py-3 text-sm border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-[#0d1117] text-gray-900 dark:text-gray-100 placeholder-gray-300 dark:placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all resize-none leading-relaxed"></textarea>
          </div>
          <div>
            <label for="org_tag_line" class="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1.5">{{ I18N.features.organization.tag_line }}</label>
            <input :value="org.tag_line"
                   @focusout="updateOrgField"
                   name="organization[tag_line]" id="org_tag_line" type="text"
                   class="w-full px-3.5 py-2.5 text-sm border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-[#0d1117] text-gray-900 dark:text-gray-100 placeholder-gray-300 dark:placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all" />
          </div>
        </div>
      </div>

      <!-- Branding card -->
      <div class="bg-white dark:bg-[#1C2128] rounded-xl border border-gray-100 dark:border-gray-700 overflow-hidden">
        <div class="px-6 py-4 border-b border-gray-100 dark:border-gray-700 flex items-center gap-3">
          <div class="w-6 h-6 rounded-md bg-gray-100 dark:bg-gray-800 flex items-center justify-center flex-shrink-0">
            <svg class="w-3.5 h-3.5 text-gray-500 dark:text-gray-400" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" d="M9.53 16.122a3 3 0 00-5.78 1.128 2.25 2.25 0 01-2.4 2.245 4.5 4.5 0 008.4-2.245c0-.399-.078-.78-.22-1.128zm0 0a15.998 15.998 0 003.388-1.62m-5.043-.025a15.994 15.994 0 011.622-3.395m3.42 3.42a15.995 15.995 0 004.764-4.648l3.876-5.814a1.151 1.151 0 00-1.597-1.597L14.146 6.32a15.996 15.996 0 00-4.649 4.763m3.42 3.42a6.776 6.776 0 00-3.42-3.42"/>
            </svg>
          </div>
          <div>
            <h2 class="text-sm font-semibold text-gray-900 dark:text-white leading-none">{{ I18N.features.organization.platform_picture }}</h2>
            <p class="text-[11px] text-gray-400 dark:text-gray-500 mt-0.5 leading-none">Shown in the navigation bar for your team</p>
          </div>
        </div>
        <div class="px-6 py-5 flex items-center gap-5">
          <AvatarUploadCard
            ref="desktopPlatformCard"
            shape="rounded"
            :picture-url="org.platform_picture_url"
            :avatar-name="org.avatar_name"
            :avatar-color-class="org.avatar_color"
            :upload-url="`/api/v1/organizations/${org.id}/platform_picture`"
            :remove-url="`/api/v1/organizations/${org.id}/remove_platform_picture`"
            form-field-name="organization[platform_picture]"
            :title="I18N.features.organization.change_platform_picture"
            @updated="onPlatformPictureUpdated"
            @error="onAvatarError"
          />
          <div>
            <button type="button" @click="$refs.desktopPlatformCard.openModal()"
              class="inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors">
              <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" d="M3 16.5v2.25A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75V16.5m-13.5-9L12 3m0 0 4.5 4.5M12 3v13.5"/>
              </svg>
              Change logo
            </button>
            <p class="mt-2 text-xs text-gray-400 dark:text-gray-500">{{ I18N.features.organization.dimensions }}: 300x100</p>
          </div>
        </div>
      </div>

      <!-- Danger zone card -->
      <div class="bg-white dark:bg-[#1C2128] rounded-xl border border-red-100 dark:border-red-900/40 overflow-hidden">
        <div class="px-6 py-4 border-b border-red-100 dark:border-red-900/40 flex items-center gap-2.5">
          <div class="w-6 h-6 rounded-md bg-red-50 dark:bg-red-900/30 flex items-center justify-center flex-shrink-0">
            <svg class="w-3.5 h-3.5 text-red-500 dark:text-red-400" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z"/>
            </svg>
          </div>
          <div>
            <h2 class="text-sm font-semibold text-red-600 dark:text-red-400 leading-none">{{ I18N.website_content.account_settings.account_tab.danger_zone }}</h2>
            <p class="text-[11px] text-red-400/70 dark:text-red-500/70 mt-0.5 leading-none">Permanent and irreversible actions</p>
          </div>
        </div>
        <div class="px-6 py-5">
          <p class="text-sm text-gray-500 dark:text-gray-400 mb-4">{{ I18N.features.organization.remove_org_subheading }}</p>
          <button @click="showDeleteModal = true" type="button"
            class="flex items-center gap-2 px-4 py-2 border border-red-300 dark:border-red-700 text-red-600 dark:text-red-400 text-sm font-semibold rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors">
            <svg class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/>
            </svg>
            {{ I18N.features.organization.remove_org }}
          </button>
        </div>
      </div>

    </div>
  </div>

  <!-- ═══════════════ DELETE ORGANIZATION MODAL ═══════════════ -->
  <Teleport to="body">
    <div v-show="showDeleteModal" class="fixed inset-0 z-40 bg-gray-900/60 backdrop-blur-sm"></div>
    <div v-show="showDeleteModal" class="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none">
      <div class="relative w-full max-w-md pointer-events-auto">
        <div class="bg-white dark:bg-[#1C2128] rounded-2xl shadow-2xl border border-gray-100 dark:border-gray-700 overflow-hidden">

          <div class="px-6 pt-6 pb-4 border-b border-gray-100 dark:border-gray-700 flex items-start justify-between gap-4">
            <div>
              <h3 class="text-base font-semibold text-gray-900 dark:text-white">{{ I18N.features.organization.remove_org }}?</h3>
              <p class="text-sm text-gray-500 dark:text-gray-400 mt-0.5">{{ I18N.features.organization.remove_org_subheading }}</p>
            </div>
            <button @click="showDeleteModal = false" type="button"
              class="w-7 h-7 rounded-lg bg-gray-100 dark:bg-gray-800 flex items-center justify-center hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors flex-shrink-0">
              <svg class="w-4 h-4 text-gray-500 dark:text-gray-400" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12"/>
              </svg>
            </button>
          </div>

          <div class="px-6 py-4 bg-gray-50 dark:bg-[#0d1117] border-t border-gray-100 dark:border-gray-700 flex gap-3 justify-end">
            <button @click="showDeleteModal = false" type="button"
              class="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-[#1C2128] border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">
              {{ I18N.application.bookings.upcoming.cancel }}
            </button>
            <button @click="deleteOrganization" type="button"
              class="px-4 py-2 text-sm font-semibold text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors flex items-center gap-2 min-w-[5rem] justify-center">
              <svg v-if="deleting" class="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
              </svg>
              <span>{{ deleting ? I18N.application.events.deleting : I18N.application.events.delete }}</span>
            </button>
          </div>

        </div>
      </div>
    </div>
  </Teleport>
</template>

<script setup>
import { ref, computed } from 'vue'
import { orgStore } from 'controllers/vue_components/organizations/store'
import { useRequest } from 'composables/useRequest.js'
import { useConstants } from 'composables/useConstants.js'
import FlashNotifications from 'controllers/vue_components/flash_toast'
import AvatarUploadCard from 'controllers/vue_components/settings/shared/AvatarUploadCard'

const flash = ref(null)
const { axiosRequest } = useRequest(flash)
const { I18N } = useConstants()
const store = orgStore()

const org = computed(() => store.current_org)
const showDeleteModal = ref(false)
const deleting = ref(false)

function onPlatformPictureUpdated(newOrgData) {
  store.$patch({ current_org: newOrgData })
}

function onAvatarError(message) {
  flash.value?.show(message, 'alert')
}

async function updateOrgField(e) {
  const [, fieldName] = e.target.name.split('[').map((p) => p.replace(']', ''))
  const fieldValue = e.target.value
  if (org.value[fieldName] === fieldValue) return

  const response = await store.updateCurrentOrg({ organization: { [fieldName]: fieldValue } })
  if (response.js_error === false) {
    if (response.response.success) {
      flash.value?.show(I18N.features.organization.account_settings_saved, 'notice')
    } else {
      flash.value?.show(response.response.message, 'alert')
    }
  } else {
    flash.value?.show(response.error?.data?.message || 'Something went wrong', 'alert')
  }
}

async function deleteOrganization() {
  deleting.value = true
  const res = await axiosRequest('DELETE', `/api/v1/organizations/${org.value.id}`, {}, false)
  if (res.js_error === false && res.response.success) {
    window.location.replace('/organizations/new')
  } else {
    flash.value?.show(res.error?.data?.message || res.response?.message || 'Something went wrong', 'alert')
    deleting.value = false
  }
}
</script>
